import { useState } from "react";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User } from "./useAdminData";

interface CreateUserData {
	email: string;
	firstName: string;
	lastName: string;
	role: "user" | "admin" | "super_admin";
	companyId: string | null;
	createNewCompany: boolean;
	newCompanyName: string;
}

interface UpdateUserData {
	active: any;
	id: string;
	firstName: string;
	lastName: string;
	role: "user" | "admin" | "super_admin";
	organizationId: string | null | undefined;
}

export const useUserManagement = (refreshData: () => void) => {

	// Track loading state
	const [isCreatingUser, setIsCreatingUser] = useState(false);

	const handleCreateUser = async (userData: CreateUserData) => {
		// Set loading state to prevent double submissions
		if (isCreatingUser) {
			//console.log("Already processing a user creation request");
			return false;
		}

		setIsCreatingUser(true);

		try {

			//console.log("Creating user with data:", userData);

			// If creating a new company
			let finalCompanyId = userData.companyId;

			if (userData.createNewCompany && userData.newCompanyName) {
				//console.log("Creating new company:", userData.newCompanyName);
				const { data: newCompany, error: companyError } = await supabase
					.from('organizations')
					.insert([{ name: userData.newCompanyName }])
					.select('id')
					.single();

				if (companyError) {
					console.error("Error creating company:", companyError);
					throw companyError;
				}

				//console.log("New company created:", newCompany);
				finalCompanyId = newCompany.id;
			}

			const isLocalhost = () => {
				return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
			};

			// Prepare request body for the edge function
			const requestBody = {
				email: userData.email,
				firstName: userData.firstName,
				lastName: userData.lastName,
				role: userData.role,
				organizationUUID: finalCompanyId,
				isDev : isLocalhost()
			};

			//console.log("Calling create-user function with:", requestBody);

			// Add a fallback direct fetch to the edge function for debugging
			try {
				// First, try with supabase.functions.invoke
				//console.log("Attempting to create user with supabase.functions.invoke...");

				try {

					

					const { data, error: functionError } = await supabase.functions.invoke('create-user', {
						body: requestBody
					});

					if (functionError) {
						console.error("Error from edge function via invoke:", functionError);
						throw new Error(functionError.message || "Failed to create user");
					}
					
					//console.log("User created successfully via invoke:", data);

				} catch (invokeError) {
					
				}

				toast({
					title: "Success",
					description: "User created successfully",
				});

				// Immediately refresh data after successful user creation
				//console.log("Refreshing data after user creation");
				refreshData();

				return true;
			} catch (error: any) {
				console.error("Network or function invocation error:", error);
				throw error;
			}
		} catch (error: any) {
			console.error("Error creating user:", error);
			toast({
				title: "Error",
				description: error.message || "Failed to create user",
				variant: "destructive",
			});
			return false;
		} finally {
			setIsCreatingUser(false);
		}
	};

	const handleEditUser = async (userData: UpdateUserData) => {
		try {
			//console.log("Updating user:", userData);

			// Update the profile
			const { error: profileError } = await supabase
				.from('profiles')
				.update({
					role: userData.role,
					first_name: userData.firstName,
					last_name: userData.lastName,
					active: userData.active,
				})
				.eq('id', userData.id);

			if (profileError) {
				console.error("Error updating profile:", profileError);
				throw profileError;
			}

			// Update the user_organization if organizationId is provided
			if (userData.organizationId) {
				const { error: orgError } = await supabase
					.from('user_organization')
					.update({
						organization_uuid: userData.organizationId,
					})
					.eq('user_uuid', userData.id);

				if (orgError) {
					console.error("Error updating user organization:", orgError);
					throw orgError;
				}
			}

			toast({
				title: "Success",
				description: "User updated successfully",
			});

			// Immediately trigger a refresh
			//console.log("Refreshing data after user update");
			setTimeout(() => {
				refreshData();
				//console.log("Data refresh completed");
			}, 2000); // Increased timeout to 2 seconds

			return true;
		} catch (error: any) {
			console.error("Error updating user:", error);
			toast({
				title: "Error",
				description: error.message || "Failed to update user",
				variant: "destructive",
			});
			return false;
		}
	};

	return {
		handleCreateUser,
		handleEditUser,
		isCreatingUser
	};
};
